import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Award, TrendingUp } from "lucide-react";

interface EmissionEntry {
  fe_key: string;
  tco2e: number;
  category?: string;
}

interface CategoryBadgesProps {
  emissions: EmissionEntry[];
  factors: Array<{ fe_key: string; category: string }>;
}

/**
 * CategoryBadges Component
 * Displays badges for each emission category with progression
 */
export function CategoryBadges({ emissions, factors }: CategoryBadgesProps) {
  // Group emissions by category
  const emissionsByCategory = emissions.reduce((acc, emission) => {
    const factor = factors.find((f) => f.fe_key === emission.fe_key);
    const category = factor?.category || "Other";
    
    if (!acc[category]) {
      acc[category] = { total: 0, count: 0 };
    }
    
    acc[category].total += emission.tco2e;
    acc[category].count += 1;
    
    return acc;
  }, {} as Record<string, { total: number; count: number }>);

  // Define badge thresholds (number of emissions needed)
  const getBadgeLevel = (count: number): { level: string; color: string; icon: string } => {
    if (count >= 10) return { level: "Master", color: "bg-yellow-500", icon: "🏆" };
    if (count >= 5) return { level: "Expert", color: "bg-blue-500", icon: "⭐" };
    if (count >= 3) return { level: "Advanced", color: "bg-green-500", icon: "✨" };
    if (count >= 1) return { level: "Beginner", color: "bg-gray-400", icon: "🌱" };
    return { level: "None", color: "bg-gray-200", icon: "" };
  };

  // Calculate progress to next level
  const getProgress = (count: number): { current: number; next: number; percentage: number } => {
    if (count >= 10) return { current: count, next: 10, percentage: 100 };
    if (count >= 5) return { current: count, next: 10, percentage: (count / 10) * 100 };
    if (count >= 3) return { current: count, next: 5, percentage: (count / 5) * 100 };
    if (count >= 1) return { current: count, next: 3, percentage: (count / 3) * 100 };
    return { current: 0, next: 1, percentage: 0 };
  };

  const categories = Object.entries(emissionsByCategory);

  if (categories.length === 0) {
    return (
      <div className="p-4 bg-muted/50 rounded-md text-center">
        <p className="text-sm text-muted-foreground">
          No emissions recorded yet. Start adding emissions to earn badges!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Award className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">Category Badges</h3>
      </div>
      
      <div className="grid grid-cols-1 gap-3">
        {categories.map(([category, data]) => {
          const badge = getBadgeLevel(data.count);
          const progress = getProgress(data.count);
          
          return (
            <div key={category} className="p-3 border rounded-md space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{badge.icon}</span>
                  <span className="font-medium text-sm">{category}</span>
                </div>
                <Badge variant="secondary" className={`${badge.color} text-white`}>
                  {badge.level}
                </Badge>
              </div>
              
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{data.count} emissions</span>
                  <span>{data.total.toFixed(4)} tCO₂e</span>
                </div>
                
                {badge.level !== "Master" && (
                  <>
                    <Progress value={progress.percentage} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      {progress.current}/{progress.next} to next level
                    </p>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      <div className="p-3 bg-primary/10 border border-primary/20 rounded-md">
        <div className="flex items-center gap-2 text-sm">
          <TrendingUp className="h-4 w-4 text-primary" />
          <span className="font-medium">Keep going!</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Add more emissions to unlock higher badges and compare with your sector
        </p>
      </div>
    </div>
  );
}

